<template>
  <div v-if="skeleton" class="rich-text-editor loading">
    <cv-skeleton-text :paragraph="true" width="100%" :line-count="lineCount" />
  </div>
  <div
    v-else
    v-click-outside:#app.capture="handleClickOutside"
    class="rich-text-editor"
    :class="richTextEditorClass"
  >
    <div v-if="!isPlainTextEditor" class="mce-toolbar" :data-id="editorId" />
    <Editor
      ref="editor"
      :tinymce-script-src="tinymceScriptSrc"
      :license-key="tinymceLicenseKey"
      :init="tinymceConfig"
      :initial-value="initialContent"
      @key-down="handleKeyDown"
      @change="handleChange"
      @undo="handleUndo"
      @redo="handleRedo"
      @paste="handlePaste"
      @input="handleInput"
    />
    <cv-button
      v-if="showFullScreenButton"
      class="icon-button btn-expand"
      kind="tertiary"
      :disabled="fullScreenDisabled"
      @click.prevent="handleOpenFullScreen"
      @mousedown.prevent
    >
      <svg-icon name="Expand16" kind="action" />
    </cv-button>

    <div v-if="showFooter" class="rich-text-editor-footer">
      <message-component v-if="hasError" class="error">
        {{ errorMessage }}
      </message-component>

      <text-word-character-count
        v-if="showWordCounter"
        :text-content="textContent"
        :show-tooltip="showWordCounterTooltip"
        :recommended-tooltip-english-char="recommendedTooltipEnglishChar"
        :recommended-tooltip-chinese-char="recommendedTooltipChineseChar"
      />
      <span v-if="showAutoSaveIndicator" class="auto-save-indicator" :class="autoSaveStateClasses">
        <component :is="autoSaveIndicatorIcon" class="sls-icon" />
        {{ autoSaveIndicatorMessage }}
      </span>
    </div>
  </div>
</template>

<script setup lang="ts">
// ─── External libraries ────────────────────────────────────────────────
import { debounceFn } from "@/utils/debounce";
import Editor from "@tinymce/tinymce-vue";

// ─── Vue core / ecosystem ──────────────────────────────────────────────
import {
  computed,
  inject,
  nextTick,
  onBeforeMount,
  onBeforeUnmount,
  onMounted,
  ref,
  useTemplateRef,
  watch,
  markRaw,
} from "vue";
import { useStore } from "vuex";
import { useI18n } from "vue-i18n";

// ─── Utilities ─────────────────────────────────────────────────────────
import DOMUtils from "@utils/dom.utils";
import RandomUtils from "@utils/random.utils";
import DeviceUtils from "@utils/device.utils";
import { isDefined, isEmpty } from "@utils/var.check";
import { clickOutsideDirective } from "@utils/directives";
import { useVm } from "@utils/vm.ts";

// ─── Common composables / helpers ──────────────────────────────────────
import { useErrorHandler } from "@plugins/common/composables/error.ts";
import { useDynamicMountParent } from "@plugins/common/composables/dynamic-mount-parent.ts";
import { useToast } from "@plugins/toast/composables/toast-instance.ts";
import { useModal } from "@plugins/modal/composables/modal-instance.ts";

// ─── Constants ─────────────────────────────────────────────────────────
import { MEDIA_TYPE } from "@plugins/media/media.constant";
import {
  AUTO_SAVE_INDICATOR_STATE,
  AutoSaveIndicatorState,
  NAMESPACE,
} from "@modules/attempt/service/attempt.constant";
import {
  COMPONENT_BAR_SUB_TYPE,
  COMPONENT_BAR_SUB_TYPE_OPTIONS,
} from "@modules/editor/service/editor.constant";
import { ANNOTATION_RICHTEXT_EDITOR_OPTIONS } from "@plugins/richtext/richtext.constant.ts";
import { ACP_PLUGINS } from "../plugins/authoring-copilot/acp-plugin-helper";

// ─── Services / EventBus ───────────────────────────────────────────────
import EventBus from "@plugins/common/service/eventbus";
import CommentEventBus from "@plugins/comment/service/event-bus";
import MentionEventBus from "@plugins/richtext/plugins/mentions/service/event-bus.ts";

// ─── API / Config / Utils for RichText ─────────────────────────────────
import richtextEditorApi from "@apis/richtext.editor.api";
import emojis from "@configs/emojis";
import tinymceConfigs, {
  getToolbarCollapsed,
  setToolbarCollapsed,
} from "@plugins/richtext/configs/tinymce";
import extendedIcons from "@plugins/richtext/assets/icons.ts";
import richTextUtils, { generateBlankId } from "@plugins/richtext/utils/richtext.utils";
import tinymceUtils from "@plugins/richtext/utils/tinymce";

// ─── RichText Plugins ──────────────────────────────────────────────────
import mylink from "@plugins/richtext/plugins/mylink/plugin.ts";
import dynamicLink from "@plugins/richtext/plugins/dynamic-link/plugin.ts";
import notebookDynamicLink from "@plugins/richtext/plugins/notebook-dynamic-link/plugin.ts";
import alignment from "@plugins/richtext/plugins/alignment/plugin.ts";
import drawing from "@plugins/richtext/plugins/drawing/plugin.ts";
import stp from "@plugins/richtext/plugins/stp/plugin.ts";
import studentTooltip from "@plugins/richtext/plugins/student-tooltip/plugin.ts";
import tooltip from "@plugins/richtext/plugins/tooltip/plugin.ts";
import youtube from "@plugins/richtext/plugins/youtube/plugin.ts";
import fibBlank from "@plugins/richtext/plugins/fib-blank/plugin.ts";
import fileUpload from "@plugins/richtext/plugins/file-uploader/plugin.ts";
import audioRecorder from "@plugins/richtext/plugins/audio-recorder/plugin.ts";
import mediaObject from "@plugins/richtext/plugins/media-object/plugin.ts";
import html5Interactive from "@plugins/richtext/plugins/html5-interactive/plugin.ts";
import google from "@plugins/richtext/plugins/google/plugin.ts";
import embedWebsite from "@plugins/richtext/plugins/embed-website/plugin.ts";
import externalMedia from "@plugins/richtext/plugins/external-media/plugin.ts";
import textToSpeech from "@plugins/richtext/plugins/text-to-speech/plugin.ts";
import speechEvaluation from "@plugins/richtext/plugins/speech-evaluation/plugin.ts";
import algebraKit from "@plugins/richtext/plugins/algebra-kit/plugin.ts";
import plaintext from "@plugins/richtext/plugins/plain-text/plugin.ts";
import characterWordCount from "@plugins/richtext/plugins/character-word-count/plugin.ts";
import customMentions from "@plugins/richtext/plugins/mentions/plugin.ts";
import acpPlugin from "@plugins/richtext/plugins/authoring-copilot/plugin.ts";
import otherFormatting from "@plugins/richtext/plugins/other-formatting/plugin.ts";

// ─── Components ────────────────────────────────────────────────────────
import TextWordCharacterCount from "@plugins/character-count/components/TextWordCharacterCount.vue";
import RichTextModal from "@plugins/richtext/components/RichTextModal.vue";
import SvgIcon from "@plugins/common/components/SvgIcon.vue";
import MessageComponent from "@plugins/common/components/MessageComponent.vue";
import Refresh16 from "@images/icons/action/16px/Refresh16.svg?component";
import Success16 from "@images/icons/indicative/16px/Success16.svg?component";
import Error16 from "@images/icons/indicative/16px/Error16.svg?component";

// ─── Types ─────────────────────────────────────────────────────────────
import {
  ChangeEventLike,
  EditorContext,
  GoogleAttachment,
  PasteArgs,
  ResourceContext,
  RichTextEditorConfig,
  RichTextErrors,
  RichTextPlugin,
  UploadedFileCount,
  WhiteList,
} from "@plugins/richtext/types.ts";
import userActivityTrackEvent from "@plugins/user-activity-track/service/user.activity.track.event.ts";

const vClickOutside = clickOutsideDirective["click-outside"];

const props = withDefaults(
  defineProps<{
    disabled?: boolean;
    skeleton?: boolean;
    lineCount?: number;
    kind?: string;
    size?: string;
    placeholder?: string;
    initialContent?: string;
    content?: string;
    config?: RichTextEditorConfig;
    isMandatory?: boolean;
    focusOnInit?: boolean;
    initialOptionType?: string;
    googleDocumentProps?: { id: number };
    ugcDocumentProp?: { uuid: string; file: File | Blob; createAsNew?: boolean } | null;
    ugcImageCanvasConfigProp?: {
      imageUrl: string;
      uuid: string;
      fileName: string;
      isNewImage?: boolean;
      createAsNew?: boolean;
      skipCropping?: boolean;
    } | null;
    enableFullScreen?: boolean;
    isFullScreen?: boolean;
    title?: string;
    enableTooltip?: boolean;
    recommendedTooltipEnglishChar?: number;
    recommendedTooltipChineseChar?: number;
    customErrorMessage?: string;
    hasSEAudio?: boolean;
    anchor?: string;
    isSEGraded?: boolean;
    disableEnterLineBreak?: boolean;
    subpageParentTitle?: string[];
    questionAttemptUuid?: string;
    customEmptyErrorMessage?: string;
    disableFibBlank?: boolean;
    fibBlankGroups?: ({ label: string; color: string } | null)[];
    isWithinExpandedModal?: boolean;
    optionsMediaList?: unknown[];
    editorId?: string;
    editorContext?: EditorContext;
    showDrawingRestriction?: boolean;
    autoSaveIndicatorState?: AutoSaveIndicatorState;
    autoSaveIndicatorMessage?: string;
  }>(),
  {
    disabled: false,
    skeleton: false,
    lineCount: 5,
    kind: "basic",
    size: "extra_small",
    placeholder: "",
    initialContent: "",
    content: "",
    config: () => ({}),
    isMandatory: false,
    focusOnInit: false,
    initialOptionType: "",
    googleDocumentProps: () => ({ id: -1 }),
    ugcDocumentProp: null,
    ugcImageCanvasConfigProp: null,
    enableFullScreen: false,
    isFullScreen: false,
    title: "",
    enableTooltip: false,
    recommendedTooltipEnglishChar: 0,
    recommendedTooltipChineseChar: 0,
    customErrorMessage: "",
    hasSEAudio: false,
    anchor: "",
    isSEGraded: false,
    disableEnterLineBreak: false,
    subpageParentTitle: () => [],
    questionAttemptUuid: "",
    customEmptyErrorMessage: "",
    disableFibBlank: false,
    fibBlankGroups: () => [],
    isWithinExpandedModal: false,
    optionsMediaList: () => [],
    editorId: undefined,
    editorContext: undefined,
    showDrawingRestriction: false,
    autoSaveIndicatorState: AUTO_SAVE_INDICATOR_STATE.IDLE,
    autoSaveIndicatorMessage: "",
  },
);

const emit = defineEmits<{
  (e: "focus"): void;
  (e: "blur", payload: string): void;
  (e: "blur-click", payload: { elementClicked: unknown }): void;
  (e: "isRecording", payload: boolean): void;
  (e: "ready", payload: string): void;
  (e: "input", payload: Event): void;
  (e: "change", payload: string, force?: boolean): void;
  (e: "changeFullScreenMode", payload: boolean): void;
  (e: "changeHtml5Interactive", payload: unknown): void;
  (e: "loadOnlyOnceModal", payload: boolean): void;
  (e: "duplicateGoogleAttachment"): void;
}>();

const { dynamicMountParentMixin_childComponents } = useDynamicMountParent();

const { t } = useI18n();
const store = useStore();
const toast = useToast();
const modal = useModal();
const { vm, vmExpose } = useVm();
const { passiveApiFailHandler } = useErrorHandler();

const richTextContext = inject("richTextContext", {} as Record<string, unknown>);
const annotationContext = inject("annotationContext", {} as Record<string, unknown>);
const questionAttemptContext = inject("questionAttemptContext", {} as Record<string, unknown>);
const resourceContext = inject<ResourceContext>("resourceContext");

const loaded = ref(false);
const editorRef = useTemplateRef("editor");
const isDisabled = ref(props.disabled);
const editorId = ref(props.editorId ? props.editorId : RandomUtils.randomId());
const plugins = ref<Record<string, RichTextPlugin>>({});

// Re-render fib blank placeholders when the group mapping changes (group/ungroup
// does not alter the editor content, so the change event would not fire on its own).
watch(
  () => props.fibBlankGroups,
  () => {
    plugins.value["fib_blank"]?.renderInEditor?.();
  },
  { deep: true },
);
const pluginEvent = ref();
const fileService = ref();
const isDestroyed = ref(false);
const errors = ref<RichTextErrors>({ isEmpty: false, isOverCharLimit: false });
const changeTimer = ref<ReturnType<typeof setTimeout> | undefined>(undefined);
const attachedFiles = ref<unknown[]>([]);
const whiteList = ref<WhiteList | null>(null);
const googleAttachments = ref<GoogleAttachment[]>([]);
const fullScreenDisabled = ref(false);
const isTextWordCharacterCountInFocus = ref(false);
const textContent = ref("");
const showCharacter = ref(true);
const tinymceLicenseKey = ref(tinymceConfigs.licenseKey);
const suppressKeyup = ref(false);

const showFullScreenButton = computed(() => !props.isFullScreen && props.enableFullScreen);
const tinymceScriptSrc = computed(() => tinymceConfigs.scriptSrc);
const getWhitelistDomainMapPromise = computed(
  () => store.getters["app/getWhitelistDomainMapPromise"],
);

const tinymceConfig = computed(() => {
  const extendedConfigs = { ...tinymceConfigs[props.kind] };

  extendedConfigs.language = tinymceUtils.getLanguage();
  extendedConfigs.placeholder = props.placeholder;

  const id = editorId.value;
  extendedConfigs.fixed_toolbar_container = `.mce-toolbar[data-id="${id}"]`;
  extendedConfigs.init_instance_callback = (editor) => {
    // TinyMCE built-in tap event listener calls e.preventDefault function
    // when user clicks on components in noneditable container.
    // Which will prevent components in noneditable container from triggering click events
    // and third party components like vue-plyr may not function correctly.
    // We need to implement custom tap event listener to override TinyMCE default behaviours.
    // TODO: need to copy the implementation from tinymce.js
    editor.off("tap");
    // have to call in init callback after instance initialized
    // as won't work if call through @onInit
    initialized(editor);

    // Track overflow button clicks to persist toolbar collapse preference.
    // NOTE: 'data-mce-name="overflow-button"' is a TinyMCE-rendered attribute used to identify the
    // toolbar drawer toggle button. While it depends on TinyMCE's internal DOM, there is no public
    // API to detect this specific user interaction. The handler is wrapped in try-catch so that any
    // future TinyMCE DOM changes degrade silently without breaking the editor.
    const toolbarEl = document.querySelector(`.mce-toolbar[data-id="${id}"]`);
    if (toolbarEl) {
      const handleToolbarClick = (event: Event) => {
        try {
          const target = event.target as HTMLElement;
          if (!target.closest('button[data-mce-name="overflow-button"]')) return;
          // State updates after the event, so read after a short delay
          setTimeout(() => {
            const isDrawerOpen = editor.queryCommandState("ToggleToolbarDrawer");
            setToolbarCollapsed(!isDrawerOpen);
          }, 200);
        } catch (e) {
          console.warn(
            "[RichTextEditor] Unable to detect toolbar overflow button click — TinyMCE internal DOM may have changed:",
            e,
          );
        }
      };
      toolbarEl.addEventListener("click", handleToolbarClick, true);
      toolbarEl.addEventListener("touchend", handleToolbarClick, true);
      editor.on("remove", () => {
        toolbarEl.removeEventListener("click", handleToolbarClick, true);
        toolbarEl.removeEventListener("touchend", handleToolbarClick, true);
      });
    }

    editor.options.set("disabled", isDisabled.value);

    if (isPlainTextEditor.value) {
      // SLSUAT-2978: disable formatting shortcuts
      tinymceConfigs.disable_shortcut_patterns.forEach((pattern: string) => {
        editor.shortcuts.add(pattern, "disable", () => ({}));
      });
    }

    if (!DeviceUtils.isMobile() && props.isWithinExpandedModal) {
      // To trigger "click" on RichTextModal tinymce instance
      editor.execCommand("mceFocus");
    }
  };

  type PastePreprocess = (editor, args: PasteArgs) => false | void;

  extendedConfigs.paste_preprocess = ((editor, args) => {
    const pastedContent = richTextUtils.removeMediaAndStripHTML(args.content);
    if (!editor) editor = tinymceEditor();

    let selectedContent = "";
    if (!isEmpty(editor)) {
      selectedContent = richTextUtils.removeMediaAndStripHTML(editor.selection.getContent());
    }

    const totalLength =
      getContentLength(getTextContent()) -
      getContentLength(selectedContent) +
      getContentLength(pastedContent);
    clearError();

    const elem = DOMUtils.createElement("div", {});
    elem.innerHTML = args.content;
    const moClass = ["sls-media", "sls-media-wrapper"];
    const pasteAllowed = { ...(whiteList.value as WhiteList) };
    const allAllowed = pasteAllowed[":all"]?.attributes.split(",") ?? [];

    let toAlert = false;
    const sanitize = (element: Element) => {
      let ele = element as HTMLElement;
      //revert img to math ml for wiris equations
      ele = richTextUtils.revertImageBackToMathML(ele);

      const tagName = isDefined(ele.tagName) ? ele.tagName.toLowerCase() : "";

      const removeAndAlert = () => {
        ele.remove();
        return true;
      };

      //  SLSUAT-7157 in macbook chrome meta charset tag is included in the pasted content from tinymce
      if (tagName === "meta" && ele.hasAttribute("charset")) {
        ele.remove();
        return false;
      }

      if (tagName === "iframe") return removeAndAlert();

      if (tagName === "input") {
        if (ele.classList.contains("fill-in-box")) {
          const newId = String(generateBlankId());
          ele.removeAttribute("data-generated");
          ele.setAttribute("id", newId);
          ele.setAttribute("style", "width: 100px;");
          return false;
        }
        return removeAndAlert();
      }

      if (tagName === "div" && moClass.some((c) => ele.classList.contains(c))) {
        return removeAndAlert();
      }

      const studentTeacherTooltipClass = [
        "sls-stp-wrapper",
        "teacher-stp",
        "sls-student-tooltip-wrapper",
        "student-tooltip",
      ];
      if (studentTeacherTooltipClass.some((c) => ele.classList.contains(c))) {
        return removeAndAlert();
      }

      if (pasteAllowed[tagName] === undefined) return removeAndAlert();

      const allowed =
        pasteAllowed[tagName].attributes === "" ? [] : pasteAllowed[tagName].attributes.split(",");

      Array.from(element.attributes).forEach((attr) => {
        if (!allowed.includes(attr.name) && !allAllowed.includes(attr.name)) {
          (element as HTMLElement).removeAttribute(attr.name);
        }
      });

      return false;
    };

    // [MOESLSOPS-11052]
    // strip HTML comment (anything that matches between <!-- and -->)
    const commentRegex = /<!--[\s\S]*?-->/g;
    if (commentRegex.test(elem.innerHTML)) {
      elem.innerHTML = elem.innerHTML.replace(commentRegex, "");
    }

    // replace namespace prefix, i.e <o:p> to <p>
    const namespacedTagRegex = /<(\w+):(\w+)([^>]*)>([\s\S]*?)<\/\1:\2>/g;
    if (namespacedTagRegex.test(elem.innerHTML)) {
      elem.innerHTML = elem.innerHTML.replace(namespacedTagRegex, "<$2$3>$4</$2>");
    }

    DOMUtils.elOf(elem, "*").forEach((el) => {
      if (sanitize(el)) toAlert = true;
    });

    args.content = elem.innerHTML;
    if (props.editorContext) {
      const pastedContent = elem.innerHTML.slice(0, 200);
      userActivityTrackEvent.paste({
        activityUuid: props.editorContext.activityUuid,
        assignmentUuid: props.editorContext.assignmentUuid,
        editorId: editorId.value,
        moduleUuid: props.editorContext.moduleUuid,
        pastedContent: pastedContent,
        pastedContentLength: elem.innerHTML.length,
        questionUuid: props.editorContext.questionUuid,
        questionType: props.editorContext.questionType,
        sectionUuid: props.editorContext.sectionUuid,
        location: props.editorContext.location,
      });
    }

    if (toAlert) {
      toast.show("warning", { title: t("tinymce.paste.stripped.message") });
    }

    if (totalLength > tinymceCharLimit.value) {
      editor.selection.collapse(false);
      nextTick(() => (errors.value.isOverCharLimit = true));
      args.content = "";
      return false;
    }
    return;
  }) as PastePreprocess;

  extendedConfigs.paste_postprocess = (editor, args: { node: Element }) => {
    if (!editor) {
      editor = tinymceEditor();
    }
    richTextUtils.convertExternalMedia(args.node);
    window.setTimeout(() => {
      richTextUtils.renderInlineExternalMedia(editor, false);
      editor.save();
    });
  };

  extendedConfigs.setup = (editor) => {
    /**
     * Custom paste implementation that ensures consistent behavior between Ctrl+V and clipboard API paste.
     * Processes HTML/text content through TinyMCE's paste pipeline to maintain proper image framing,
     * external media conversion, and content wrapping.
     */
    const customPasteAction = async () => {
      try {
        const clipboardItems = await navigator.clipboard.read();

        for (const item of clipboardItems) {
          let content = "";

          if (item.types.includes("text/html")) {
            const htmlBlob = await item.getType("text/html");
            content = await htmlBlob.text();
          } else if (item.types.includes("text/plain")) {
            const textBlob = await item.getType("text/plain");
            content = await textBlob.text();
          }

          if (content) {
            const pasteArgs: PasteArgs = { content };

            if (extendedConfigs.paste_preprocess) {
              const result = extendedConfigs.paste_preprocess(editor, pasteArgs);
              if (result === false) return;
            }

            const tempDiv = document.createElement("div");
            tempDiv.innerHTML = pasteArgs.content;

            if (extendedConfigs.paste_postprocess) {
              extendedConfigs.paste_postprocess(editor, { node: tempDiv });
            }

            const tagsToCleanStyle = ["em", "strong"];
            const cleanStyles = (element: Element) => {
              if (element.nodeType === Node.ELEMENT_NODE) {
                let el = element as HTMLElement;
                if (
                  !el.classList.contains("sls-external-media") &&
                  !el.classList.contains("sls-media") &&
                  !el.classList.contains("sls-media-wrapper") &&
                  !el.classList.contains("loaded")
                ) {
                  el.removeAttribute("style");
                  el.removeAttribute("class");

                  if (tagsToCleanStyle.includes(el.tagName.toLowerCase())) {
                    const newElement = DOMUtils.createElement("span");
                    newElement.innerHTML = el.innerHTML;
                    el.parentNode?.replaceChild(newElement, el);
                    el = newElement;
                  }
                }
                Array.from(el.children).forEach(cleanStyles);
              }
            };

            if (editor.queryCommandState("mceTogglePlainTextPaste")) {
              cleanStyles(tempDiv);
            }

            const finalContent = tempDiv.innerHTML;
            editor.insertContent(finalContent);

            window.setTimeout(() => {
              richTextUtils.renderInlineExternalMedia(editor, false);
              editor.save();
            }, 0);

            handlePaste({ type: "paste" }, editor);
            return;
          }
        }
      } catch (error) {
        console.error("Custom paste failed:", error);
      }
    };

    editor.addCommand("Paste", function () {
      customPasteAction();
    });

    editor.addCommand("mceInsertClipboardContent", function () {
      customPasteAction();
    });

    // Button below editor doesn't trigger click event when user focuses on editor and clicks the button.
    // It is because editor hides the toolbar on blur and the button position is changed.
    // We override editor's default behaviour and hide the toolbar when user clicks on another editor.
    registerCustomEditorOptions(editor);

    const langCode = (editor.options.get("language") as string) || "en";
    window.tinymce.addI18n(langCode, {
      "Background color": "Highlight text",
      "Background color {0}": "Highlight text",
      "Background color menu": "Highlight text menu",
      "Line height": "Line spacing",
    });

    const iconKeys = Object.keys(extendedIcons);
    iconKeys.forEach(function (iconKey) {
      editor.ui.registry.addIcon(iconKey, extendedIcons[iconKey]);
    });
    editor.on("focus", () => {
      // Check if there is any disabled options upon first focus, if yes will focus again
      // Use aria-disabled attribute on toolbar buttons — more stable than TinyMCE internal CSS class names
      const container = editor.getContainer();
      const hasDisabled = container.querySelector("button[aria-disabled='true']") !== null;
      if (hasDisabled) {
        editor.focus();
      }
      emit("focus");
      isTextWordCharacterCountInFocus.value = true;
      if (window.tinymce.blurEditor) {
        if (window.tinymce.blurEditor.isDirty()) {
          window.tinymce.blurEditor.save();
        }
        delete window.tinymce.blurEditor;
      }

      const shouldExpand = !getToolbarCollapsed();
      const isDrawerOpen = editor.queryCommandState("ToggleToolbarDrawer");

      // Sync drawer state with stored preference in both directions:
      // expand if preference is expanded but drawer is closed, or
      // collapse if preference is collapsed but drawer is open.
      // toggle toolbar drawer has native behavior of autofocus (highlight) the first toolbar button after open the drawer
      // add skipFocus to prevent it from autofocus, caret stays on the editor
      if (shouldExpand !== isDrawerOpen) {
        setTimeout(() => {
          // Re-check inside the timeout: handleUpdateFromFullScreen may have already synced
          // the drawer (e.g. on expanded modal close), avoiding an unintended double-toggle.
          const currentDrawerOpen = editor.queryCommandState("ToggleToolbarDrawer");
          if (shouldExpand !== currentDrawerOpen) {
            editor.execCommand("ToggleToolbarDrawer", false, { skipFocus: true });
          }
        }, 100);
      }

      if (!DeviceUtils.isMobile()) {
        if (props.isWithinExpandedModal) {
          // tinymce fires a blur natively. refocus as workaround
          // otherwise users need to click twice to collapse toolbar after loading.
          // The 500ms delay is intentionally longer than the 100ms toolbar sync above
          // so the drawer state is settled before this refocus triggers a second focus event.
          setTimeout(() => {
            editor.execCommand("mceFocus");
          }, 500);
        }
      }
    });

    editor.on("blur", () => {
      window.tinymce.blurEditor = editor;
      // SLSUAT-2243: upon open wiris modal, blur event will get trigger
      // not trigger blur event if wiris modal open since editor still active
      const canBlur = DOMUtils.isWhitelistedPluginNotPresent();

      if (canBlur) {
        const content = editor.getContent();
        const freshContent = getFreshContent(content);
        emit("blur", freshContent);
        setTimeout(() => {
          emit("blur-click", {
            elementClicked: window.tinymce.clickedElement,
          });
        }, 200);
      }

      return false;
    });

    // TO BE REVIEW
    // As non-drag scenarios (e.g. click) somehow will trigger the drag event
    // based on tinymce dragging behaviors, it will append a ghost element
    // and bind on "mousemove" event
    // causing the ghost image keep moving with mouse cursor, like dragging
    editor.on("dragstart", (e: ChangeEventLike) => {
      // when it goes into tinymce mouse move event,
      // it will skip the ghost element being appended if isDefaultPrevent is true.
      e.preventDefault?.();
    });

    editor.on("click", (event: { target }) => {
      const firstChild = event.target.firstChild;
      const isParagraph = event.target.classList.contains("paragraph");
      const hasLineBreak = firstChild && firstChild.tagName && firstChild.tagName === "BR";
      if (isParagraph && hasLineBreak) {
        const elements = editor.dom.select('div[data-mce-bogus="all"]');
        elements.forEach((element) => {
          element.parentNode.removeChild(element);
        });
      }
      // cater for cases when have undo action MOESLSOPS-4143
      for (const plugin of Object.keys(plugins.value)) {
        if (plugins.value[plugin] && plugins.value[plugin].handlePluginRerender) {
          plugins.value[plugin].handlePluginRerender(event.target);
        }
      }
    });

    // Chrome on Android device does not block user input when character limit is reached
    // because event.preventDefault function call in handleKeyDown is not working.
    // We need to manually delete last character from editor content to block user input.
    const isIosDevice = DeviceUtils.isIos();
    const handleManualSave = debounceFn(
      () => {
        editor.save();
      },
      500,
      { leading: false, trailing: true },
    );
    editor.on("keydown", (event: ChangeEventLike) => {
      // do not allow the editor to insert line break
      // when user press "Enter"
      const code = event.code || event.key;
      if (props.disableEnterLineBreak && richTextUtils.isEnterKeystroke(code) && !event.shiftKey) {
        const target = event.target as HTMLElement | undefined;
        const richTextEditorElement = target
          ? (DOMUtils.parent(target, ".rich-text-editor") as HTMLElement | undefined)
          : undefined;
        const mentionMenuItems = richTextEditorElement?.querySelectorAll(".sls-mention-name");

        if (!isEmpty(mentionMenuItems)) {
          suppressKeyup.value = true;
          return;
        }

        event.preventDefault();
        event.stopPropagation();
        return false;
      }
    });

    editor.on("keyup", (event: ChangeEventLike) => {
      if (suppressKeyup.value) {
        event.preventDefault?.();
        event.stopPropagation?.();
        suppressKeyup.value = false;
        return;
      }
    });

    editor.on("input", () => {
      // IOS device allow user to add suggestion words even no text is key in yet
      // and tinymce cannot detect this adding suggestion word behavior
      // turns out placeholder is still showing
      // we manually remove placeholder whenever there is input event
      const body = editor.getBody();
      const empty = isEmptyField();
      const hasPlaceholder = body.hasAttribute("data-mce-placeholder");

      if (empty && hasPlaceholder) {
        body.removeAttribute("data-mce-placeholder");
        body.removeAttribute("aria-placeholder");
        // we need call save event as suggestion word won't fire change event
        editor.save();
      }

      // For iOS and ipadOS, using on-screen word suggestion need to manually trigger change event
      if (empty && isIosDevice) {
        handleManualSave();
      }

      if (!errors.value.isOverCharLimit) {
        return;
      }

      const textContentLength = getContentLength(getTextContent());
      if (textContentLength > tinymceCharLimit.value) {
        const selection = editor.selection.getSel();
        const range = editor.selection.getRng();
        const container = range.startContainer;

        if (container.nodeType === Node.TEXT_NODE) {
          const exceedLength = textContentLength - tinymceCharLimit.value;
          const startOffset = container.length > exceedLength ? container.length - exceedLength : 0;
          const endOffset = container.length;
          range.setStart(container, startOffset);
          range.setEnd(container, endOffset);
          range.deleteContents();
          selection.removeRange(range);
        }
      }
    });

    window.tinymce.PluginManager.add("fib_blank", (editor) => {
      fibBlank(editor);
    });
    window.tinymce.PluginManager.add("mylink", mylink);
    window.tinymce.PluginManager.add("dynamic_link", dynamicLink);
    window.tinymce.PluginManager.add("notebook_dynamic_link", notebookDynamicLink);
    window.tinymce.PluginManager.add("alignment", alignment);
    window.tinymce.PluginManager.add("drawing", drawing);
    window.tinymce.PluginManager.add("stp", stp);
    window.tinymce.PluginManager.add("tooltip", tooltip);
    window.tinymce.PluginManager.add("student_tooltip", studentTooltip);
    window.tinymce.PluginManager.add("file_upload", fileUpload);
    window.tinymce.PluginManager.add("youtube", youtube);
    window.tinymce.PluginManager.add("recording", audioRecorder);
    window.tinymce.PluginManager.add("media_object", mediaObject);
    window.tinymce.PluginManager.add("html5_interactive", html5Interactive);
    window.tinymce.PluginManager.add("google", google);
    window.tinymce.PluginManager.add("embed_website", embedWebsite);
    window.tinymce.PluginManager.add("external_media", externalMedia);
    window.tinymce.PluginManager.add("text_to_speech", textToSpeech);
    window.tinymce.PluginManager.add("speech_evaluation", speechEvaluation);
    window.tinymce.PluginManager.add("algebra_kit", algebraKit);
    window.tinymce.PluginManager.add("plaintext", plaintext);
    window.tinymce.PluginManager.add("character_word_count", characterWordCount);
    window.tinymce.PluginManager.add("custom_mentions", customMentions);
    window.tinymce.PluginManager.add("other_formatting", otherFormatting);
    window.tinymce.Resource.add("tinymce.plugins.emoticons", emojis);

    ACP_PLUGINS.forEach(({ key, showPlugin }) => {
      if (showPlugin()) {
        window.tinymce.PluginManager.add(key, (editor) => {
          acpPlugin(editor, key);
        });
      }
    });
  };

  return {
    ...tinymceConfigs.base,
    ...extendedConfigs,
  };
});

const tinymceCharLimit = computed(() => tinymceEditor()?.options.get("size").character_limit);
const richTextEditorClass = computed(() => ({
  loaded: loaded.value,
  _error: hasError.value,
  "has-autosave-indicator": showAutoSaveIndicator.value,
  "footer-shown": showFooter.value,
}));
const hasError = computed(() => Object.values(errors.value).some((v) => v));
const errorMessage = computed(() => {
  if (!hasError.value) {
    return "";
  }

  if (!isEmpty(props.customErrorMessage)) {
    return props.customErrorMessage;
  }

  if (errors.value.isEmpty) {
    // case when field is empty
    return !isEmpty(props.customEmptyErrorMessage)
      ? props.customEmptyErrorMessage
      : t("field_empty_error_message");
  } else if (errors.value.isOverCharLimit) {
    // case when field has exceeded allowed character limit.
    return t("character_limit_invalid_message", {
      num: tinymceCharLimit.value,
    });
  } else if (errors.value.isMissingPluginElement) {
    return t(errors.value.isMissingPluginElement);
  }

  return "";
});
const isPlainTextEditor = computed(() => props.kind === "plaintext");
const showWordCounter = computed(() => {
  // Add handling to show word counter if focus is on the close button (cv-modal.vue onShown method) which causes the editor to lose focus therefore hiding the word count
  return (
    (isTextWordCharacterCountInFocus.value ||
      (document.activeElement &&
        (document.activeElement as HTMLElement).classList &&
        (document.activeElement as HTMLElement).classList.contains("bx--modal-close"))) &&
    "character_word_count" in plugins.value
  );
});
const hasRecommendedTooltipCharacter = computed(
  () => props.recommendedTooltipEnglishChar !== 0 && props.recommendedTooltipChineseChar !== 0,
);
const showWordCounterTooltip = computed(
  () =>
    (showWordCounter.value && props.enableTooltip && hasRecommendedTooltipCharacter.value) ?? false,
);
const showFooter = computed(
  () => hasError.value || showWordCounter.value || showAutoSaveIndicator.value,
);
const html5MOUuid = computed(
  () => getProp(props.config, "plugins.html5_interactive.html5MOUuid") ?? null,
);
const autoSaveIndicatorIcon = computed(() => {
  if (props.autoSaveIndicatorState === AUTO_SAVE_INDICATOR_STATE.SAVING) {
    return markRaw(Refresh16);
  }

  if (props.autoSaveIndicatorState === AUTO_SAVE_INDICATOR_STATE.SAVED) {
    return markRaw(Success16);
  }

  if (props.autoSaveIndicatorState === AUTO_SAVE_INDICATOR_STATE.ERROR) {
    return markRaw(Error16);
  }

  return "";
});

const autoSaveStateClasses = computed(() => {
  if (props.autoSaveIndicatorState === AUTO_SAVE_INDICATOR_STATE.SAVING) {
    return "saving";
  }

  if (props.autoSaveIndicatorState === AUTO_SAVE_INDICATOR_STATE.SAVED) {
    return "saved";
  }

  if (props.autoSaveIndicatorState === AUTO_SAVE_INDICATOR_STATE.ERROR) {
    return "error";
  }

  return "";
});
const showAutoSaveIndicator = computed(
  () => props.autoSaveIndicatorState !== AUTO_SAVE_INDICATOR_STATE.IDLE,
);

watch(
  () => props.content,
  (newContent) => {
    const c = newContent === null ? "" : newContent;
    tinymceEditor()?.setContent(c);
    // SLSSIT-6130: clean all button, setContent() above somehow clean up the content
    // put placeholder checking after
    // related with the fix SLSUAT-2005
    if (!isEmptyField()) {
      const body = tinymceEditor()?.getBody();
      body.removeAttribute("data-mce-placeholder");
      body.removeAttribute("aria-placeholder");
    }

    textContent.value = c;

    render();
  },
);

watch(
  () => props.disabled,
  () => {
    isDisabled.value = props.disabled;
    if (tinymceEditor()) {
      tinymceEditor()?.options.set("disabled", isDisabled.value);
      if (isDisabled.value) {
        setPlaceholder();
      }
    }
  },
);

watch(
  () => props.googleDocumentProps,
  (data) => {
    // insert google attachment from outside of plugin:
    // first insert trigger init method at Google/plugin.ts
    // subsequent insert trigger watch
    if (!isEmpty(data.id)) plugins.value["google"]?.insertMarkGoogleAttachment?.(data);
  },
);

watch(
  () => props.ugcDocumentProp ?? null,
  (data) => {
    if (data) {
      plugins.value["fileUpload"]?.handleFileUploadHidden?.({
        uuid: data.uuid,
        file: data.file,
        createAsNew: data.createAsNew,
        onFail: passiveApiFailHandler,
      });
    }
  },
);

watch(
  () => props.ugcImageCanvasConfigProp ?? null,
  (data) => {
    if (data) {
      plugins.value["drawing"]?.openCanvasModal?.(data);
    }
  },
);

watch(html5MOUuid, () => {
  if (plugins.value["html5_interactive"]) {
    const htmlMOUUid = html5MOUuid.value;
    plugins.value["html5_interactive"]?.initializeHTML5MOUuid?.(htmlMOUUid);
  }
});

onBeforeMount(() => {
  const freshContent = getFreshContent(props.initialContent);
  if (!isEmpty(freshContent)) {
    textContent.value = richTextUtils.removeMediaAndStripHTML(freshContent);
  }
});

onMounted(() => {
  if (!isEmpty(props.googleDocumentProps) && !isEmpty(props.googleDocumentProps.id)) {
    plugins.value["google"]?.insertMarkGoogleAttachment?.(props.googleDocumentProps);
  }
});

onBeforeUnmount(() => {
  isDestroyed.value = true;
  for (const plugin of Object.keys(plugins.value)) {
    if (plugins.value[plugin] && plugins.value[plugin].destroy) {
      plugins.value[plugin].destroy?.();
    }
  }
  closeCurrentWirisInstance();
});

function initWhitelistDomainMapPromise(payload?: unknown) {
  return store.dispatch(`${NAMESPACE.APP}/initWhitelistDomainMapPromise`, payload);
}

function tinymceEditor() {
  return editorRef.value?.getEditor();
}

function handleImageReady() {
  CommentEventBus.$emit("updateTimeStamp", {});
}

function handleMention(data: unknown) {
  MentionEventBus.$emit("handleMention", data);
}

function handleUpdateRecordStatus(status: boolean) {
  fullScreenDisabled.value = status;
  emit("isRecording", status);
}

// https://www.tiny.cloud/docs/tinymce/6/apis/tinymce.editoroptions/
function registerCustomEditorOptions(editor) {
  const options = editor?.options;
  options?.register("size", { processor: "object", default: tinymceConfigs.size[props.size] });
  const mediaList = !isEmpty(props.optionsMediaList) ? props.optionsMediaList : [];
  options?.register("mediaList", { processor: "object[]", default: mediaList });
  options?.register("richTextEditor", { processor: "object", default: vm });
  options?.register("pluginEvent", { processor: "object", default: new EventBus() });
}

function getProp(obj, path: string) {
  return path
    .split(".")
    .reduce((acc, key) => (acc && acc[key] != null ? acc[key] : undefined), obj);
}

async function initialized(editor) {
  // MOESLSOPS-4287: clean up dirty data to make sure there are no unsupported attributes from existing data
  const content = editor.getContent();
  whiteList.value = (await getWhiteList()) as WhiteList;
  const processedContent = processContent(content, whiteList.value as WhiteList);
  editor.setContent(processedContent);

  // SLSUAT-2005: setContent() above somehow clean up the content
  // put placeholder checking after
  if (!isEmptyField()) {
    const body = editor.getBody();
    body.removeAttribute("data-mce-placeholder");
    body.removeAttribute("aria-placeholder");
  }

  // If html5 interactive is enabled, initialize the media object UUID
  if (plugins.value["html5_interactive"]) {
    const uuid = getProp(props, "config.plugins.html5_interactive.html5MOUuid");
    plugins.value["html5_interactive"]?.initializeHTML5MOUuid?.(uuid);
    emit("changeHtml5Interactive", uuid);
  }

  // SLSSIT-14479 - include placeholder when editor mode is disabled(readOnly mode)
  if (isDisabled.value) {
    setPlaceholder();
  }

  render();

  emit("duplicateGoogleAttachment");

  await nextTick(() => {
    handleInitialOption();
  });
  loaded.value = true;
  if (props.focusOnInit) {
    window.tinyMCE.get(editor.id).focus();
  }

  // MOESLSOPS-11052: Update question value after cleanup
  // To handle case when user saves without making changes on the field containing dirty data
  if (content !== processedContent) {
    const freshContent = getFreshContent(processedContent);
    emit("change", freshContent, true);
  }

  emit("ready", editor.id);
}

function handleInitialOption() {
  switch (props.initialOptionType) {
    case COMPONENT_BAR_SUB_TYPE.FILE_FROM_DEVICE.key: {
      plugins.value["mediaObject"]?.openModal?.();
      break;
    }
    case COMPONENT_BAR_SUB_TYPE.YOUTUBE.key: {
      plugins.value["youtube"]?.openModal?.();
      break;
    }
    case COMPONENT_BAR_SUB_TYPE.WEBSITE.key: {
      plugins.value["embedWebsite"]?.openWebsiteModal?.();
      break;
    }
    case COMPONENT_BAR_SUB_TYPE.APP.key: {
      plugins.value["embedWebsite"]?.openBrowseAppSubpage?.();
      break;
    }
    case COMPONENT_BAR_SUB_TYPE_OPTIONS.FRQ_GOOGLE.key:
    case COMPONENT_BAR_SUB_TYPE_OPTIONS.ADD_FILE.key: {
      plugins.value["google"]?.openAddFromGoogle?.();
      break;
    }
    case COMPONENT_BAR_SUB_TYPE_OPTIONS.EMBED_FILE.key: {
      plugins.value["google"]?.openEmbedFromGoogle?.();
      break;
    }
    case COMPONENT_BAR_SUB_TYPE_OPTIONS.FRQ_AK.key: {
      plugins.value["algebrakit"]?.openAlgebraKitModal?.();
      break;
    }
    case COMPONENT_BAR_SUB_TYPE_OPTIONS.ARQ_SE_EN.key:
    case COMPONENT_BAR_SUB_TYPE_OPTIONS.ARQ_SE_ZH.key:
    case COMPONENT_BAR_SUB_TYPE_OPTIONS.ARQ_SE_MS.key:
    case COMPONENT_BAR_SUB_TYPE_OPTIONS.ARQ_SE_TA.key: {
      plugins.value["speech_evaluation"]?.openSpeechEvaluationModal?.();
      break;
    }
    case ANNOTATION_RICHTEXT_EDITOR_OPTIONS.DRAWING: {
      plugins.value["drawing"]?.openCanvasModal?.();
      break;
    }
    case ANNOTATION_RICHTEXT_EDITOR_OPTIONS.RECORDING: {
      plugins.value["recording"]?.startAudioRecording?.();
      break;
    }
    default: {
      return;
    }
  }
}

function handleUndo(event: ChangeEventLike, editor) {
  handleChange(event, editor);
}

function handleRedo(event: ChangeEventLike, editor) {
  handleChange(event, editor);
}

function handlePaste(event: ChangeEventLike, editor) {
  handleChange(event, editor);
}

function handleChange(event: ChangeEventLike, editor?) {
  // for some reason, file/arq will trigger handleChange when there is an ugc attachment in the content before its destroyed
  // which causes the setDirty to trigger
  if (isDestroyed.value) {
    return;
  }

  if (!editor) {
    editor = tinymceEditor();
  }

  if (props.kind == "teacher_tinymce_2") {
    for (const plugin of Object.keys(plugins.value)) {
      if (plugins.value[plugin] && plugins.value[plugin].processOnChange) {
        plugins.value[plugin].processOnChange(editor);
      }
    }
  } else if (plugins.value["customMentions"] && plugins.value["customMentions"].processOnChange) {
    plugins.value["customMentions"].processOnChange(editor);
  }

  const content = editor.getContent();
  const freshContent = getFreshContent(content);
  emit("change", freshContent);

  // re-check document status if it is still processing upon redo / undo
  if (event.type === "undo" || event.type === "redo") {
    googleAttachments.value.forEach((googleAttachment) =>
      googleAttachment.checkGoogleDocumentStatus?.(),
    );
    if (event.type === "redo") {
      handleRerenderRecording(freshContent);
    }
  }

  if (changeTimer.value) clearTimeout(changeTimer.value);

  textContent.value = richTextUtils.removeMediaAndStripHTML(freshContent);
  clearError();

  const newContentLength = getContentLength(getTextContent());
  errors.value.isOverCharLimit = newContentLength > tinymceCharLimit.value;
}

function handleRerenderRecording(freshContent: string) {
  // create temp container
  const tempContainer = document.createElement("div");
  tempContainer.innerHTML = freshContent;

  // get all media elements within freshContent
  const allMediaElements = tempContainer.querySelectorAll(".sls-media");

  for (const mediaElement of Array.from(allMediaElements)) {
    // Check if it's an audio media
    if ((mediaElement as HTMLElement).getAttribute("media-type") === "AUDIO") {
      if (plugins.value && plugins.value["recording"]) {
        plugins.value["recording"].renderInEditor?.(true);
      }
      break; // Stop the loop
    }
  }
}

function handleInput(event: Event) {
  // New content length = current content length + new keydown input length
  const newContentLength = getContentLength(getTextContent());
  errors.value.isOverCharLimit = newContentLength > tinymceCharLimit.value;
  if (errors.value.isOverCharLimit) {
    event.preventDefault();
    return false as unknown as void;
  } else {
    tinymceEditor()?.setDirty(true);
    clearError();
  }
  emit("input", event);
}

function handleKeyDown(event: ChangeEventLike, editor) {
  const code = event.code || event.key;

  // Due to handleChange occasionally not firing as expected.
  if (changeTimer.value) clearTimeout(changeTimer.value);
  changeTimer.value = setTimeout(() => {
    handleChange(event, editor);
  }, 1000);

  if (richTextUtils.isNonCharacterKeystroke(code)) {
    return;
  }

  const selection = editor.selection.getSel();

  if (!selection.isCollapsed) {
    return;
  }
}

function handleOpenFullScreen() {
  const content = tinymceEditor()?.getContent();
  const freshContent = getFreshContent(content);
  const optionsMediaList = tinymceEditor()?.options.get("mediaList");

  closeCurrentWirisInstance();
  modal.show(
    RichTextModal,
    {
      richTextContext,
      title: props.title,
      disabled: props.disabled,
      skeleton: props.skeleton,
      lineCount: props.lineCount,
      kind: props.kind,
      size: props.size,
      placeholder: props.placeholder,
      initialContent: freshContent,
      content: props.content,
      config: props.config,
      isMandatory: props.isMandatory,
      focusOnInit: props.focusOnInit,
      initialOptionType: "",
      googleDocumentProps: props.googleDocumentProps,
      enableTooltip: props.enableTooltip,
      recommendedTooltipEnglishChar: props.recommendedTooltipEnglishChar,
      recommendedTooltipChineseChar: props.recommendedTooltipChineseChar,
      autoSaveIndicatorState: () => props.autoSaveIndicatorState,
      autoSaveIndicatorMessage: () => props.autoSaveIndicatorMessage ?? "",
      optionsMediaList: optionsMediaList,
      actionHidden: (newContent: string) => {
        handleUpdateFromFullScreen(newContent, true);
      },
      handleUpdateAnswer: (newContent: string) => {
        handleUpdateFromFullScreen(newContent);
      },
    },
    {},
    true,
  );

  emit("changeFullScreenMode", true);
}

function handleUpdateFromFullScreen(newContent: string, isClosing = false) {
  emit("changeFullScreenMode", false);

  const freshContent = getFreshContent(newContent);
  emit("change", freshContent);

  tinymceEditor()?.setContent(freshContent);
  textContent.value = isEmpty(freshContent)
    ? ""
    : richTextUtils.removeMediaAndStripHTML(freshContent);
  if (!isEmptyField()) {
    const body = tinymceEditor()?.getBody();
    body.removeAttribute("data-mce-placeholder");
    body.removeAttribute("aria-placeholder");
  }

  if (isClosing) {
    // Sync toolbar drawer state with stored preference after closing the expanded modal.
    // The user may have toggled the toolbar inside the expanded view — reflect the
    // updated preference in this (the originating) editor without requiring a re-focus.
    // Only runs on close (actionHidden), not on every content change (handleUpdateAnswer),
    // to avoid repeated toggling on a non-focused editor.
    const shouldExpand = !getToolbarCollapsed();
    const isDrawerOpen = tinymceEditor()?.queryCommandState("ToggleToolbarDrawer");
    if (shouldExpand !== isDrawerOpen) {
      tinymceEditor()?.execCommand("ToggleToolbarDrawer", false, { skipFocus: true });
    }
  }

  render();
}

function getFreshContent(content: string) {
  let freshContent = content;
  for (const plugin of Object.keys(plugins.value)) {
    if (plugins.value[plugin] && plugins.value[plugin].cleanUp) {
      freshContent = plugins.value[plugin].cleanUp(freshContent) as string;
    }
  }
  return freshContent;
}

function getTextContent() {
  const content = tinymceEditor()?.getContent();
  const freshContent = getFreshContent(content);
  return richTextUtils.removeMediaAndStripHTML(freshContent);
}

function getSpeechEvaluationText() {
  const content = tinymceEditor()?.getContent();
  const container = document.createElement("div");
  container.innerHTML = content;
  const seElements = DOMUtils.elOf(container, ".sls-speech-evaluation");
  if (seElements.length === 0) return "";
  return richTextUtils.removeMediaAndStripHTML(seElements[0].innerHTML).trim();
}

async function getWhiteList() {
  let wl: WhiteList | undefined;
  const response = (await loadWhiteList()) as { data: WhiteList };
  if (response && response.data) {
    wl = response.data as WhiteList;
    try {
      // try-catch here as setItem will throw exception if storage is full
      sessionStorage.setItem("whiteList", JSON.stringify(response.data));
    } catch (e) {
      console.log("Error unable to set whiteList: " + e);
    }
  }
  return wl;
}

function processContent(content: string, wl: WhiteList) {
  if (isEmpty(wl) || isEmpty(wl["div"]) || isEmpty(wl["div"]["attributes"])) {
    return content;
  }

  // MOESLSOPS-4287: clean up dirty data
  // NOTE: do not remove!!!
  const supportedDivAttributeList = wl["div"]["attributes"] + "," + wl[":all"]["attributes"];
  const strippedElement = richTextUtils.stripUnsupportedDivAttributes(
    content,
    supportedDivAttributeList,
  );

  // [MOESLSOPS-11052]
  richTextUtils.stripUnsupportedContent(strippedElement);

  richTextUtils.convertLegacyMediaObjects(strippedElement);
  richTextUtils.convertLegacyImageMediaObjects(strippedElement);
  richTextUtils.convertLegacyExternalResource(strippedElement);

  return strippedElement.innerHTML;
}

function render() {
  for (const plugin of Object.keys(plugins.value)) {
    if (plugins.value[plugin] && plugins.value[plugin].renderInEditor) {
      plugins.value[plugin].renderInEditor?.();
    }
  }
}

function clearError() {
  Object.keys(errors.value).forEach((errTypeKey) => {
    errors.value[errTypeKey] = false;
  });
}

function validate() {
  clearError();
  validateFieldEmpty();
  validateFieldExceedCharLimit();
  validateFieldContainMandatoryPlugin();
  return !hasError.value;
}

function validateFieldEmpty() {
  errors.value.isEmpty = props.isMandatory && isEmptyField();
  return !errors.value.isEmpty;
}

function validateFieldExceedCharLimit() {
  errors.value.isOverCharLimit = getContentLength(getTextContent()) > tinymceCharLimit.value;
  return !errors.value.isOverCharLimit;
}

function validateFieldContainMandatoryPlugin() {
  if (!tinymceConfig.value.isMissingPluginElement) return true;

  const htmlContent = tinymceEditor()?.getContent();
  const container = DOMUtils.createElement("div", {});
  container.innerHTML = htmlContent;

  for (const plugin of Object.keys(tinymceConfig.value.isMissingPluginElement)) {
    if (
      plugins.value[plugin] &&
      plugins.value[plugin].containPluginElements &&
      !plugins.value[plugin].containPluginElements(container)
    ) {
      errors.value.isMissingPluginElement =
        tinymceConfig.value.isMissingPluginElement[plugin].errorMsg;
      break;
    }
  }
  // errors object's key has value or is true when there is an error.
  // returned value is true if there is an error, hence inverted object key's value.
  return !errors.value.isMissingPluginElement;
}

function isEmptyField() {
  const htmlContent = tinymceEditor()?.getContent();
  const elementContent = DOMUtils.createElement("div", {});
  elementContent.innerHTML = htmlContent;
  if (richTextUtils.containWirisPluginElements(elementContent)) return false;
  if (richTextUtils.containPluginElements(elementContent, plugins.value)) return false;
  if (richTextUtils.containCommonElements(htmlContent)) return false;
  const content = elementContent.textContent?.replace(/\n$/, ""); // removes trailing newline (tinyMCE behavior)
  return content === undefined || content.trim() === "";
}

function calculateUploadedFiles(): UploadedFileCount {
  const uploadedFileCount: UploadedFileCount = {};
  const container = document.createElement("div");
  container.innerHTML = tinymceEditor()?.getContent();
  let selector: string | null = null;
  if (
    props.kind === "teacher_tinymce_7" ||
    props.kind === "teacher_tinymce_9" ||
    props.kind === "teacher_tinymce_16"
  ) {
    selector = "div[data-type='mo']";
  } else if (
    props.kind === "student_tinymce_2" ||
    props.kind === "teacher_tinymce_11" ||
    props.kind === "student_teacher_tinymce_3" ||
    props.kind === "student_teacher_tinymce_4"
  ) {
    selector = "div[data-type='ugc']";
  }
  if (selector !== null) {
    DOMUtils.elOf(container, selector).forEach((element) => {
      const mediaType = element.getAttribute("media-type");
      switch (mediaType) {
        case "AUDIO":
          uploadedFileCount["audio"] = Number.isInteger(uploadedFileCount["audio"])
            ? (uploadedFileCount["audio"] as number) + 1
            : 1;
          break;
        case "VIDEO":
          uploadedFileCount["video"] = Number.isInteger(uploadedFileCount["video"])
            ? (uploadedFileCount["video"] as number) + 1
            : 1;
          break;
        case "IMAGE":
          break;
        default:
          uploadedFileCount["other"] = Number.isInteger(uploadedFileCount["other"])
            ? (uploadedFileCount["other"] as number) + 1
            : 1;
          break;
      }
    });
  }
  return uploadedFileCount;
}

function getUploadedVideoCount(uploadedFileCount: UploadedFileCount) {
  return Number.isInteger(uploadedFileCount["video"]) ? (uploadedFileCount["video"] as number) : 0;
}

function getUploadedAudioCount(uploadedFileCount: UploadedFileCount) {
  return Number.isInteger(uploadedFileCount["audio"]) ? (uploadedFileCount["audio"] as number) : 0;
}

function getUploadedFileCount(uploadedFileCount: UploadedFileCount) {
  return Number.isInteger(uploadedFileCount["other"]) ? (uploadedFileCount["other"] as number) : 0;
}

function getUploadedGoogleAttachmentCount() {
  const container = document.createElement("div");
  container.innerHTML = tinymceEditor()?.getContent();
  return DOMUtils.elOf(container, "google-attachment, .google-attachment").length;
}

function allowUpload(mediaType: string) {
  if (!props.config || !props.config.fileLimit) return true;
  const allowToUpload = true;
  if (mediaType === MEDIA_TYPE.IMAGE) return allowToUpload;
  const uploadedFileCount = calculateUploadedFiles();
  if (props.config.fileLimit.all) {
    // check file limit of all type
    const totalFileUploaded =
      getUploadedFileCount(uploadedFileCount) +
      getUploadedVideoCount(uploadedFileCount) +
      getUploadedAudioCount(uploadedFileCount);
    if (totalFileUploaded >= props.config.fileLimit.all) return !allowToUpload;
  }
  if (props.config.fileLimit.audioAndVideo) {
    // check file limit of audio and video type
    if (
      getUploadedVideoCount(uploadedFileCount) + getUploadedAudioCount(uploadedFileCount) >=
      props.config.fileLimit.audioAndVideo
    )
      return !allowToUpload;
  }
  return allowToUpload;
}

function loadWhiteList() {
  return new Promise((resolve) => {
    const wl = sessionStorage.getItem("whiteList");
    if (wl) {
      resolve({ data: JSON.parse(wl) });
    } else {
      richtextEditorApi.fetchWhiteList().then((response: unknown) => {
        resolve(response);
      }, passiveApiFailHandler);
    }
  });
}

// google attachment
function getGoogleDocumentProps() {
  return props.googleDocumentProps;
}

function getContentLength(textContent: string | undefined) {
  return textContent?.replace(/\n/g, "")?.length ?? 0;
}

function handleClickOutside() {
  isTextWordCharacterCountInFocus.value = false;
  closeCurrentWirisInstance();
}

function closeCurrentWirisInstance() {
  if (isEmpty(window?.WirisPlugin?.instances)) {
    return; // no wiris instances
  }
  const editorElementId = editorRef.value?.$el?.id ?? null;
  const wirisInstance = window.WirisPlugin?.instances[editorElementId]; // get editor's wiris instance
  if (isEmpty(wirisInstance) || isEmpty(editorElementId)) {
    return; // current instance not found
  }
  if (typeof wirisInstance?.core?.modalDialog?.close === "function") {
    if (typeof wirisInstance.core.modalDialog.popup?.cancelAction === "function") {
      wirisInstance.core.modalDialog.popup?.cancelAction(); // close any popup in wiris window
    }
    wirisInstance.core.modalDialog.close(); // close wiris window
  }
}

function setPlaceholder() {
  if (isEmptyField() && !isEmpty(props.placeholder)) {
    const body = tinymceEditor()?.getBody();
    body.setAttribute("data-mce-placeholder", props.placeholder);
    body.setAttribute("aria-placeholder", props.placeholder);
  }
}

vmExpose({
  richTextContext,
  annotationContext,
  questionAttemptContext,
  resourceContext,
  googleAttachments,
  store,
  pluginEvent,
  fileService,
  attachedFiles,
  textContent,
  showCharacter,
  getWhitelistDomainMapPromise,
  initWhitelistDomainMapPromise,
  tinymceEditor,
  handleImageReady,
  handleMention,
  handleUpdateRecordStatus,
  initialized,
  getTextContent,
  getSpeechEvaluationText,
  render,
  clearError,
  validate,
  isEmpty: isEmptyField,
  getUploadedGoogleAttachmentCount,
  allowUpload,
  getGoogleDocumentProps,
  handleClickOutside,
  plugins: plugins,
  modal,
  dynamicMountParentMixin_childComponents,
  passiveApiFailHandler,
  html5MOUuid,
});

defineExpose({
  richTextContext,
  annotationContext,
  questionAttemptContext,
  resourceContext,
  googleAttachments,
  attachedFiles,
  clearError,
  getTextContent,
  validate,
  handleRerenderRecording,
  handleUndo,
  handleRedo,
  handlePaste,
  handleChange,
  handleInput,
  handleKeyDown,
  handleOpenFullScreen,
  handleUpdateFromFullScreen,
  getFreshContent,
  processContent,
  setPlaceholder,
  plugins,
  store,
  modal,
  errors,
  tinymceEditor,
  html5MOUuid,
});
</script>
